import { USER_ROLES } from '@/constants';
import { $canOrg, $can, $canSub } from '@pendo/permissions';
import { fetchHasOnboardingAccess } from '@/onboarding-homepage/helpers/onboarding-access/onboarding-access.helper';
import useOnboarding from '@/onboarding-homepage/use/use-onboarding';
import { PRODUCT_LINES } from '@/settings/subscription-settings/product-lines';
import {
    buildAppSettingsTabAccessContext,
    hasManagementAccessForAppIds,
    hasManagementAccessForAppId,
} from '@/admin/admin.utils';
import { useOrganizationStore } from '@/stores/organization.store';
import { useVocItemsStore } from '@/stores/voc-items.store';
import { useUserSettingsStore } from '@/stores/user-settings.store';
import { useAppsStore } from '@/stores/apps.store';

import { useSubscriptionStore } from '@/stores/subscription.store';
import {
    isAgentToolkitBlocked as isAgentToolkitBlockedFor,
    isAgentToolkitDiscoverable as isAgentToolkitDiscoverableFor,
} from '@/guides/agent-toolkit/multi-agent/atk-entitlement';
import { useProductLinesStore } from '@/stores/product-lines.store';

import { useAuthStore } from '@/stores/auth.store';

export const SUBSCRIPTION_PLANS = {
    PENDO_FREE: 'pendoFree',
    PENDO_STARTER: 'pendoStarter',
    PENDO_STARTER_2024: 'pendoStarter2024',
    PENDO_FREE_TRIAL: 'pendoFreeTrial',
};

export const ADDITIONAL_ROLES = {
    ORG_ADMIN: 'ORG_ADMIN',
    OPS: 'OPS',
    PENDOZER: 'pendozer',
    ADOPT_MANAGER: 'adoptManager',
};

export function hasFeatureFlagsEnabled () {
    return useSubscriptionStore().hasFeatureFlagsEnabled;
}

export function hasFeatureFlag (featureFlag) {
    return useSubscriptionStore().getFeatureFlag(featureFlag);
}

export function canSubmitFeedback () {
    return useVocItemsStore().canSubmitFeedback;
}

export function hasSegmentFlag (segmentFlag) {
    return useAuthStore().hasSegmentFlag(segmentFlag);
}

export function hasProductLine (productLine, metadataName) {
    return useProductLinesStore().hasProductLinePurchased(productLine, metadataName);
}

export function isScimEnabled () {
    return useSubscriptionStore().isScimEnabled;
}

export function hasUserPermissions (permissionsLevel) {
    switch (permissionsLevel) {
        case USER_ROLES.ADMIN:
            return useAuthStore().hasAdminAccess;
        case ADDITIONAL_ROLES.OPS:
            return useAuthStore().isOps;
        case ADDITIONAL_ROLES.ORG_ADMIN:
            return useAuthStore().isAuthorizedForRoute('ORG_ADMIN');
        case USER_ROLES.SUPER:
            return useAuthStore().hasSuperAccess || useAuthStore().isOps;
        case ADDITIONAL_ROLES.PENDOZER:
            return useAuthStore().isPendozer;
        case ADDITIONAL_ROLES.ADOPT_MANAGER:
            return useAuthStore().isAdoptManager;
    }
}

export function isAdmin () {
    return hasUserPermissions(USER_ROLES.ADMIN);
}

export function canManageServiceAccounts () {
    return useAuthStore().canManageServiceAccounts;
}

export function isSupport () {
    return useAuthStore().isSupport;
}

// the subscription tiers and AI opt-outs the backend refuses agentToolKit for (APP-168811)
export function isAgentToolkitBlocked () {
    const subscription = useSubscriptionStore();

    return isAgentToolkitBlockedFor({
        getFeatureFlag: subscription.getFeatureFlag,
        isPendoFree: subscription.isPendoFree,
        isInTrial: subscription.isInTrial,
        isStarterPackage: subscription.isStarterPackage,
        isStarter2024Package: subscription.isStarter2024Package,
    });
}

// pre-launch gate shared with the side-nav item: toolkit on, or the enableAtkCustomAgent segment
export function isAgentToolkitDiscoverable () {
    const subscription = useSubscriptionStore();

    return isAgentToolkitDiscoverableFor({
        hasSegmentFlag: useAuthStore().hasSegmentFlag,
        getFeatureFlag: subscription.getFeatureFlag,
        isPendoFree: subscription.isPendoFree,
        isInTrial: subscription.isInTrial,
        isStarterPackage: subscription.isStarterPackage,
        isStarter2024Package: subscription.isStarter2024Package,
    });
}

export function isAdoptOnCore () {
    return useSubscriptionStore().isAdoptOnCore;
}

export function isDigitalAdoptionOnly () {
    return useSubscriptionStore().isDigitalAdoptionOnly;
}

export function isPartnerAnalyticsSub () {
    return useSubscriptionStore().isPartnerAnalytics;
}

export function isRouteDisabledForSubscriptionPlan (disabledSubscriptionPlans) {
    return disabledSubscriptionPlans.some((disabledSubscriptionPlan) => {
        switch (disabledSubscriptionPlan) {
            case SUBSCRIPTION_PLANS.PENDO_FREE:
                return useSubscriptionStore().isPendoFree;
            case SUBSCRIPTION_PLANS.PENDO_FREE_TRIAL:
                return useSubscriptionStore().isInTrial;
            case SUBSCRIPTION_PLANS.PENDO_STARTER:
                return useSubscriptionStore().isStarterPackage;
            case SUBSCRIPTION_PLANS.PENDO_STARTER_2024:
                return useSubscriptionStore().isStarter2024Package;
            default:
                return false;
        }
    });
}

export function hasOrgTabsPermissions (tabName) {
    const hasOrgSettingsAccess = $canOrg().entity('organizationSetting').action('view');

    const hasOrgResourcesAccess = $canOrg().entity('organizationResources').action('edit');

    const { organizationFlags } = useOrganizationStore().getActive;

    if (!organizationFlags) {
        return false;
    }

    if (
        tabName !== 'mau'
        && tabName !== 'oauthApprovals'
        && (hasUserPermissions(USER_ROLES.SUPER) || hasUserPermissions(ADDITIONAL_ROLES.OPS))
    ) {
        return true;
    }

    switch (tabName) {
        case 'mau':
            return (
                hasSegmentFlag('orgMauVisible') &&
                organizationFlags.enableMauView &&
                (hasUserPermissions(USER_ROLES.SUPER) ||
                    hasUserPermissions(ADDITIONAL_ROLES.OPS) ||
                    hasOrgSettingsAccess)
            );
        case 'orgLibrary':
            return (
                hasOrgResourcesAccess &&
                organizationFlags.enableResourceSharing &&
                hasProductLine('guides', 'copyThemeEnabled')
            );
        case 'users':
            return hasOrgSettingsAccess;
        case 'sso':
            return hasOrgSettingsAccess && (organizationFlags.enableSaml || isScimEnabled());
        case 'domainVerification':
            return (
                hasOrgSettingsAccess &&
                organizationFlags.enableSaml &&
                hasSegmentFlag('domainVerification')
            );
        case 'cname':
            return hasOrgSettingsAccess && organizationFlags.enableCname;
        case 'planDetails':
            return hasOrgSettingsAccess && organizationFlags.enablePlanDetails;
        case 'essentialContacts':
            return hasOrgSettingsAccess;
        case 'oauthApprovals':
            return (
                hasOrgSettingsAccess
                || hasUserPermissions(USER_ROLES.SUPER)
                || hasUserPermissions(ADDITIONAL_ROLES.OPS)
            );
        default:
            return false;
    }
}

export function beforeEnterGuardRedirectByAfter (to) {
    if (!useAuthStore().isAuthenticated) return;

    if (to.query.after) {
        const afterUri = decodeURIComponent(to.query.after);
        // Hi, this check is important, specifically that we're looking for `/in-app/`
        // at the START of the uri - outside of needing to make sure the user
        // wants to go to the classic designer under normal usage, we also need to
        // ensure a malicious actor can't set `after` to `some-bad-domain.com` to force
        // a redirect.
        if (afterUri.indexOf('/in-app/') === 0) {
            window.location.href = afterUri;
        }
    }
}

export function buildUnauthenticatedLoginRedirectRoute () {
    const searchParams = new URLSearchParams(window.location.search);
    const afterParam = searchParams.get('after');

    const query = {};

    if (afterParam) {
        query.after = encodeURIComponent(afterParam);
    } else {
        const pathQuery = String(window.location.href).replace(window.location.origin, '');
        query.after = encodeURIComponent(pathQuery);
    }

    return {
        to: 'Login',
        query,
    };
}

export function adminRouteSubscriptionPostChecks ({ isAdoptOnCoreAllowed = false } = {}) {
    const onSampleData = useSubscriptionStore().getFeatureFlag('sampleDataSubscription');
    const { preSampleDataSubId } = useAuthStore();

    if (onSampleData && preSampleDataSubId !== null) {
        return { name: 'Home' };
    }

    const subIsAdoptOnCore = useSubscriptionStore().isAdoptOnCore;

    if (!isAdoptOnCoreAllowed && subIsAdoptOnCore && !hasUserPermissions(ADDITIONAL_ROLES.OPS)) {
        return { name: 'NoAccess' };
    }

    return null;
}

export function adminRoutesGuard ({ isAdoptOnCoreAllowed = false } = {}) {
    if (!hasUserPermissions(USER_ROLES.ADMIN)) return { name: 'NoAccess' };

    return adminRouteSubscriptionPostChecks({ isAdoptOnCoreAllowed });
}

export function betasRouteGuard () {
    if (!hasSegmentFlag('betaEnrollmentPage')) return { name: 'NotFound' };
    if (hasProductLine('adoptEndUser') && !hasUserPermissions(ADDITIONAL_ROLES.OPS)) return { name: 'NoAccess' };

    return adminRoutesGuard();
}

function getAppSettingsTabAccessContext ({ hasAdminAccess = false } = {}) {
    return buildAppSettingsTabAccessContext({
        featureAvailability: useSubscriptionStore().appSettingsFeatureAvailability,
        hasAdminAccess,
        hasSessionReplayViewerAccess: useAuthStore().hasSessionReplayViewerAccess(),
        hideRawEvents: useSubscriptionStore().getFeatureFlag('hideRawEvents'),
        isPendoFree: useSubscriptionStore().isPendoFree,
        isInTrial: useSubscriptionStore().isInTrial,
        isAdoptEndUser: useSubscriptionStore().isAdoptEndUser,
        enableCustomUserRoles: useSubscriptionStore().getFeatureFlag('enableCustomUserRoles'),
    });
}

export function appAdminRouteGuard (to) {
    const appId = to?.params?.appId;

    if (!appId) return { name: 'NoAccess' };

    if (hasProductLine(PRODUCT_LINES.ADOPT)) {
        return { name: 'NoAccess' };
    }

    const isAdmin = hasUserPermissions(USER_ROLES.ADMIN);
    const app = useAppsStore().getAppById(appId);
    const hasManagementAccess = hasManagementAccessForAppId(
        appId,
        app,
        getAppSettingsTabAccessContext({ hasAdminAccess: isAdmin }),
    );

    if (!isAdmin && !hasManagementAccess) {
        return { name: 'NoAccess' };
    }

    return adminRouteSubscriptionPostChecks();
}

export function hasResourceCenterAccess () {
    const actions = ['create', 'edit', 'publish', 'remove'];

    const canView = useAppsStore().list.some((app) => {
        return actions.some((action) => {
            return $can(app.id).entity('resourceCenter').action(action).source('ANY').target('ANY');
        });
    });

    return canView && hasFeatureFlag('enableP2Access');
}

export function hasGuideThemesAccess () {
    const hasGuidesProductLine = hasProductLine('guides');
    const isAdmin = hasUserPermissions(USER_ROLES.ADMIN);
    const actions = ['create', 'edit', 'remove'];
    const hasGuideThemeAccess = actions.some((action) => {
        return $canSub().entity('guideTheme').action(action);
    });

    return hasGuidesProductLine && (isAdmin || hasGuideThemeAccess);
}

export function canAccessDomainSettings () {
    const hasOrchestrateEmailDomain = hasProductLine('orchestrate');

    if (hasOrchestrateEmailDomain) {
        return true;
    }

    const hasVocEmailDomain = hasProductLine('voc', 'email') && hasFeatureFlag('vocEmail');

    if (hasVocEmailDomain) {
        return true;
    }

    const hasSurveyEmailDomain = hasProductLine('surveys')
        && hasFeatureFlag('surveyDeliveryViaOrchestrateEmails')
        && hasFeatureFlag('vocEmail');

    return !!hasSurveyEmailDomain;
}

export function canAccessCmek () {
    return hasFeatureFlag('cmek');
}

export function canAccessClassicFeedbackExport () {
    return useSubscriptionStore().active.vendorId !== undefined;
}

export function hasSessionReplayViewerAccess () {
    return useAuthStore().hasSessionReplayViewerAccess();
}

export function hasApplicationManagementAccess () {
    return hasManagementAccessForAppIds(
        useAppsStore().list,
        getAppSettingsTabAccessContext({
            hasAdminAccess: hasUserPermissions(USER_ROLES.ADMIN),
        }),
    );
}

export function hasProductAreaAdminAccess () {
    const actions = ['create', 'edit', 'remove'];

    return actions.some((action) => $canSub().entity('productArea').action(action));
}

export function hasDataSyncAccess () {
    const actions = ['create', 'edit', 'remove'];

    return actions.some((action) => $canSub().entity('dataSync').action(action));
}

export function hasManageExcludeListAccess () {
    const actions = ['create', 'edit', 'remove'];

    return actions.some((action) => $canSub().entity('blacklist').action(action));
}

export function hasViewExcludeListAccess () {
    return $canSub().entity('blacklist').action('view');
}

export function excludeListRouteGuard ({ isAdoptOnCoreAllowed = false } = {}) {
    if (!hasUserPermissions(USER_ROLES.ADMIN) && !hasManageExcludeListAccess() && !hasViewExcludeListAccess()) {
        return { name: 'NoAccess' };
    }

    return adminRouteSubscriptionPostChecks({ isAdoptOnCoreAllowed });
}

export function hasConfigureStagingAccess () {
    const actions = ['create', 'edit', 'remove'];

    return actions.some((action) => $canSub().entity('staging').action(action));
}

export function stagingRouteGuard ({ isAdoptOnCoreAllowed = false } = {}) {
    if (!hasUserPermissions(USER_ROLES.ADMIN) && !hasConfigureStagingAccess()) {
        return { name: 'NoAccess' };
    }

    return adminRouteSubscriptionPostChecks({ isAdoptOnCoreAllowed });
}

export function hasManageEventPropertiesAccess () {
    const actions = ['create', 'edit', 'remove'];

    return actions.some((action) => $canSub().entity('eventProperty').action(action));
}

export function eventPropertiesRouteGuard ({ isAdoptOnCoreAllowed = false } = {}) {
    if (!hasUserPermissions(USER_ROLES.ADMIN) && !hasManageEventPropertiesAccess()) {
        return { name: 'NoAccess' };
    }

    return adminRouteSubscriptionPostChecks({ isAdoptOnCoreAllowed });
}

export function hasAgentModeAccess () {
    return (hasFeatureFlag('askPendo') && hasSegmentFlag('askPendo') && !hasProductLine(PRODUCT_LINES.ADOPT));
}

export function hasCommandCenterViewAccess () {
    if (!hasSegmentFlag('commandCenterAccessAllowed')) {
        return false;
    }

    if (useAuthStore().hasAdminAccess) {
        return true;
    }

    if (hasSegmentFlag('commandCenterUserPermission')) {
        return $canSub().entity('commandCenter').action('view');
    }

    return hasSegmentFlag('nonAdminsCommandCenter');
}

export function canEditSomePage () {
    const { webList } = useAppsStore();
    const { extensionList } = useAppsStore();
    const allApps = [...webList, ...extensionList];

    return allApps.some((app) => {
        return $can(app.id).entity('page').action('edit');
    });
}

export function canEditPagesForAppId (appId) {
    return $can(appId).entity('page').action('edit');
}

export async function homeBeforeEnter (to) {
    const isAEUSub = hasProductLine(PRODUCT_LINES.ADOPT);

    const { hasOnboardingAccess, setHasOnboardingAccess } = useOnboarding();
    const needsOnboardingAccessCheck = hasOnboardingAccess.value === null && !isAEUSub;

    async function loadOnboardingAccess () {
        const canAccess = await fetchHasOnboardingAccess();
        setHasOnboardingAccess(canAccess);
    }

    await Promise.all([
        needsOnboardingAccessCheck ? loadOnboardingAccess() : null,
        useAuthStore().waitForUserLoad(),
    ]);

    const isNewUser = !useAuthStore().user.useCase && !useAuthStore().user.jobRole;
    const query = isNewUser ? { ...to.query, welcome: true } : { ...to.query };
    const { params } = to;

    if (hasOnboardingAccess.value && !isAEUSub) {
        return { name: 'OnboardingHomepage', params, query };
    }

    if (hasAgentModeAccess()) {
        return { name: 'LeoHomepage', params, query };
    }

    const homeDashboardId = useUserSettingsStore().settingByName('homeDashboardId', true)?.value;
    if (homeDashboardId) {
        return { name: 'Dashboard', params: { ...params, id: homeDashboardId }, query };
    }

    if (!hasProductLine(PRODUCT_LINES.CORE, 'homepageEnabled') || isAEUSub) {
        if (isNewUser && !isAEUSub) {
            return { name: 'PostRegisterSteps', params, query };
        }

        return { name: 'DefaultDashboard', params, query };
    }

    return { name: 'Homepage', params, query };
}

// Live replay is per app, so whether the page has anything to show is only known after every
// replay-enabled app's settings load. Mirrors the nav gate: the subscription flag that exposes
// the setting, plus at least one app the setting is actually turned on for.
export async function liveReplaysBeforeEnter () {
    if (!hasProductLine(PRODUCT_LINES.SESSION_REPLAY, 'liveReplayEnabled') || !hasSegmentFlag('liveReplay')) {
        return { name: 'NotFound' };
    }

    // required lazily so the router's boot graph does not pull in the session replay store
    const { useSessionReplayStore } = require('@/stores/session-replay.store');
    const sessionReplayStore = useSessionReplayStore();

    await sessionReplayStore.loadReplaySettingsForReplayEnabledApps();

    if (!sessionReplayStore.liveReplayApps.length) {
        return { name: 'NotFound' };
    }
}

export function homepageBeforeEnter (to) {
    const correctRoute = hasAgentModeAccess() ? 'LeoHomepage' : 'Homepage';

    if (to.name !== correctRoute) {
        return { name: correctRoute, params: to.params, query: to.query };
    }
}
